import React, { useEffect, useRef, useState } from "react";

import type { ClientId } from "../../domain/types";
import {
  cancelContactImport,
  closeContactImport,
  commitContactImport,
  downloadContactTemplate,
  fetchContactImport,
  uploadContactSheet,
  type ContactImportReviewDTO,
  type ContactPushDTO,
  type Party,
} from "../../data/contactsApi";
import { hasBeenPushed } from "../../domain/contacts";
import { Callout } from "../../components/Callout";
import { CoButton } from "../../components/Button";
import { DownloadIcon, SheetIcon, UploadIcon } from "../../components/icons";
import { cn } from "../../components/cn";
import { ContactImportRows } from "./ContactImportRows";
import { ContactImportSummary } from "./ContactImportSummary";

/**
 * Bulk import: download the contact book, edit it, upload it back, push it.
 *
 * THE DOWNLOAD IS THE TEMPLATE. There is no blank form — the sheet arrives
 * holding what Zoho currently has, every row carrying its own Zoho id. That is
 * what makes the round trip safe: a row with an id is an edit to a party we can
 * name, and a row without one is unambiguously an addition.
 *
 * It is also the fastest way to clear the exceptions above it. Forty missing
 * GSTINs are forty inline fixes one at a time, or one column pasted into a
 * sheet.
 *
 * UPLOAD REACHES NOTHING. It parses, diffs and stores; the answer is what WOULD
 * change. Only the push writes, only from the confirm below, and nothing it does
 * can be undone.
 *
 * One of these per party, side by side on the Upload tab, and they are
 * independent: a customer sheet under review does not disturb a vendor sheet
 * beside it.
 */

type Phase = "loading" | "idle" | "uploading" | "review";

/**
 * How many batches one press of Push is allowed to run.
 *
 * The loop exists because Zoho has no bulk create and one HTTP request cannot
 * hold two hundred sequential writes. The cap exists because a loop driven by a
 * server field is a loop that hammers the server if that field ever gets stuck —
 * the page stops and offers Continue rather than spinning. Twenty-five rows a
 * batch makes this a five-thousand-row ceiling, which is the parser's own limit.
 */
const MAX_BATCHES = 200;

export function ContactImportPanel({
  clientId,
  party,
  clientName,
  organisationName,
  onPushed,
  onChanged,
}: {
  clientId: ClientId;
  party: Party;
  clientName: string;
  /** Named on the push button, because "which books am I writing to" is the question. */
  organisationName?: string | null;
  /**
   * A push finished and the list above this panel is now out of date. Without
   * this the CA pushes, sees "added 12", scrolls up to find them and sees the
   * old list, which reads as the push not having worked.
   */
  onPushed?: () => void;
  /**
   * Called after any event that changes what is staged or pushed — a successful
   * upload, a discard, each commit response, a close — so a parent keeping a
   * status badge for this sheet knows to re-read it.
   */
  onChanged?: () => void;
}) {
  const [phase, setPhase] = useState<Phase>("loading");
  const [review, setReview] = useState<ContactImportReviewDTO | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [push, setPush] = useState<ContactPushDTO | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // A push spans several requests. If the panel unmounts part-way — the CA
  // switched to the other party tab — the remaining batches must not keep
  // firing into a screen nobody is watching.
  const liveRef = useRef(true);

  useEffect(() => {
    liveRef.current = true;
    setPhase("loading");
    setPush(null);
    setConfirming(false);
    setError(null);

    fetchContactImport(clientId, party)
      .then((result) => {
        if (!liveRef.current) return;
        setReview(result);
        setPhase(result.import === null ? "idle" : "review");
      })
      .catch(() => {
        // A review we could not load is not worth an error banner: the panel
        // still works, it just starts empty.
        if (liveRef.current) setPhase("idle");
      });

    return () => {
      liveRef.current = false;
    };
  }, [clientId, party]);

  function download() {
    setBusy(true);
    setError(null);

    downloadContactTemplate(clientId, party)
      .catch((cause: unknown) => {
        setError(cause instanceof Error ? cause.message : "Could not build the sheet.");
      })
      .finally(() => setBusy(false));
  }

  function upload(file: File) {
    setPhase("uploading");
    setError(null);
    setPush(null);
    setConfirming(false);

    uploadContactSheet(clientId, party, file)
      .then((result) => {
        setReview(result);
        setPhase("review");
        onChanged?.();
      })
      .catch((cause: unknown) => {
        // Every message the server sends here is written for a CA — "that is
        // the vendors sheet, upload it under vendors" — so it goes on screen as
        // it is.
        setError(cause instanceof Error ? cause.message : "Could not read that sheet.");
        setPhase(review?.import ? "review" : "idle");
      })
      .finally(() => {
        // Otherwise picking the same file twice after a fix fires no change event.
        if (inputRef.current) inputRef.current.value = "";
      });
  }

  function discard() {
    setBusy(true);

    cancelContactImport(clientId, party)
      .then((result) => {
        setReview(result);
        setPhase("idle");
        setError(null);
        setPush(null);
        setConfirming(false);
        onChanged?.();
      })
      .catch((cause: unknown) => {
        setError(cause instanceof Error ? cause.message : "Could not discard the import.");
      })
      .finally(() => setBusy(false));
  }

  function close() {
    setBusy(true);

    closeContactImport(clientId, party)
      .then((result) => {
        setReview(result);
        setPhase("idle");
        setPush(null);
        setError(null);
        onChanged?.();
      })
      .catch((cause: unknown) => {
        setError(cause instanceof Error ? cause.message : "Could not close the import.");
      })
      .finally(() => setBusy(false));
  }

  /**
   * Run the push to completion, or to the first reason to stop.
   *
   * Sequential and deliberately so — each call tells us what is left, and
   * overlapping two would have them both read the same pending rows.
   */
  async function runPush(acknowledgeDuplicate: boolean) {
    setConfirming(false);
    setPushing(true);
    setError(null);

    try {
      // The server's queue only holds rows it can still act on, so
      // "committing" always means real work is left and every batch moves
      // forward. There is no longer a no-progress case for the browser to
      // detect: the loop that used to watch the settled count for a number
      // that stopped moving existed because refused rows were re-offered
      // forever, and they are not any more.

      for (let batch = 0; batch < MAX_BATCHES; batch++) {
        const result = await commitContactImport(clientId, party, acknowledgeDuplicate);
        if (!liveRef.current) return;

        setReview(result);
        setPush(result.push ?? null);
        onChanged?.();

        // Halted is not "keep going slower" — it is a rate limit or a refused
        // credential, and the next call would hit the same wall.
        if (!result.push || result.push.status === "done" || result.push.halted) {
          // The server re-read the contacts from Zoho as the last act of the
          // push, so the parent only has to pick that up. Not on a halt or a
          // partial batch: mid-push our stored copy is still the pre-push one.
          if (result.push?.status === "done") onPushed?.();
          break;
        }
      }
    } catch (cause: unknown) {
      if (!liveRef.current) return;

      setError(cause instanceof Error ? cause.message : "The push stopped.");

      // Whatever went wrong, the truth about which rows made it is on the
      // server, not in whatever we last rendered.
      await fetchContactImport(clientId, party)
        .then((fresh) => {
          if (liveRef.current) {
            setReview(fresh);
            // A failed batch can still have landed contacts before it broke.
            onChanged?.();
          }
        })
        .catch(() => undefined);
    } finally {
      if (liveRef.current) setPushing(false);
    }
  }

  function onDrop(event: React.DragEvent) {
    event.preventDefault();
    setDragging(false);

    const file = event.dataTransfer.files[0];
    if (file) upload(file);
  }

  const noun = party === "vendor" ? "vendors" : "customers";

  if (phase === "loading") {
    return (
      <section className="flex flex-col gap-3 rounded-lg border p-4">
        <h3 className="text-base font-medium">Bulk upload</h3>
        <p className="text-sm text-muted-foreground">Loading…</p>
      </section>
    );
  }

  const summary = review?.import ?? null;
  const rows = review?.rows ?? [];
  const pushed = summary !== null && hasBeenPushed(summary);
  const toPush = summary === null ? 0 : summary.new_rows + summary.changed_rows;
  const books = organisationName ?? `${clientName}'s Zoho Books`;
  const retryable = summary !== null && summary.status === "committing";

  return (
    <section className="flex flex-col gap-4 rounded-lg border p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <h3 className="text-base font-medium">Bulk upload {noun}</h3>
          <p className="text-sm text-muted-foreground">
            Download {clientName}'s {noun}, edit them in Excel, and upload the sheet back. Rows keep
            their Zoho ID, so an edited row updates and a new row is added.
          </p>
        </div>
        <CoButton
          variant="outline"
          onClick={download}
          disabled={busy || pushing || phase === "uploading"}
        >
          <DownloadIcon className="size-4" />
          Download sheet
        </CoButton>
      </div>

      {error !== null && <Callout variant="warning">{error}</Callout>}

      {summary === null ? (
        /*
          A label wrapping a hidden input rather than a button: it gives keyboard
          and screen-reader behaviour for free, which a div with an onClick does
          not.

          `relative` is load-bearing, not styling. The input below is `sr-only`,
          which is `position: absolute`, and without a positioned ancestor it
          resolves against the BODY — escaping the step's scroll container and
          stretching the page. See LedgerImportPanel, where this cost an
          afternoon.
        */
        <label
          onDragOver={(event) => {
            event.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          className={cn(
            "relative flex cursor-pointer flex-col items-center gap-1.5 rounded-md border border-dashed p-6 text-center transition-colors",
            dragging ? "border-primary bg-primary/5" : "hover:bg-muted/40",
            phase === "uploading" && "pointer-events-none opacity-70"
          )}
        >
          <UploadIcon className="size-5 text-muted-foreground" />
          <span className="text-sm font-medium">
            {phase === "uploading"
              ? "Reading the sheet…"
              : "Drop the filled-in sheet here, or click to choose"}
          </span>
          <span className="text-xs text-muted-foreground">.xlsx or .csv, up to 8 MB</span>
          <input
            ref={inputRef}
            type="file"
            accept=".xlsx,.csv"
            className="sr-only"
            disabled={phase === "uploading"}
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) upload(file);
            }}
          />
        </label>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <SheetIcon className="size-4 shrink-0 text-muted-foreground" />
            <span className="font-medium">{summary.original_filename}</span>
            <span className="text-xs text-muted-foreground">{summary.total_rows} rows read</span>
          </div>

          <ContactImportSummary
            summary={summary}
            rows={rows}
            party={party}
            push={push}
            pushing={pushing}
          />
          <ContactImportRows rows={rows} showOutcomes={pushed} />

          <div className="flex flex-col gap-3 border-t pt-4">
            {confirming ? (
              <PushConfirm
                count={toPush}
                books={books}
                noun={noun}
                alreadyPushedAt={summary.already_pushed_at}
                onConfirm={() => void runPush(true)}
                onCancel={() => setConfirming(false)}
              />
            ) : (
              <div className="flex flex-wrap items-center gap-3">
                {summary.status === "done" ? (
                  <>
                    <CoButton onClick={close} disabled={busy}>
                      Close
                    </CoButton>
                    {summary.failed_rows > 0 && (
                      <span className="text-xs text-muted-foreground">
                        Fix the refused rows in the sheet and upload it again.
                      </span>
                    )}
                  </>
                ) : (
                  <>
                    {/*
                      One click, straight to Zoho — the review above IS the
                      confirmation. The CA uploaded this sheet, read a diff, and
                      is looking at the rows by name and line number. A dialog
                      restating that adds no fact, and a confirm that never says
                      anything new is one people learn to click through.

                      The button names the organisation instead, because that is
                      the one thing the review never shows and the one mistake
                      worth catching: a seat can be reset and re-pointed, and
                      "whose books" is not answerable from a list of contacts.
                    */}
                    <CoButton
                      onClick={() =>
                        summary.already_pushed_at !== null ? setConfirming(true) : void runPush(false)
                      }
                      disabled={pushing || busy || toPush === 0}
                    >
                      {pushing
                        ? "Pushing…"
                        : retryable
                          ? `Continue - ${push?.remaining ?? "some"} left`
                          : `Push ${toPush} to ${books}`}
                    </CoButton>
                    <CoButton variant="outline" onClick={discard} disabled={busy || pushing}>
                      Discard
                    </CoButton>
                  </>
                )}
              </div>
            )}
          </div>
        </>
      )}
    </section>
  );
}

/**
 * The gate for pushing a file that has already been pushed.
 *
 * NOT a generic "are you sure" — an ordinary push does not come through here.
 * This exists for the one case where the screen knows something the CA
 * demonstrably does not, because if they knew it they would not be pressing the
 * button: this exact spreadsheet has been through already, and Zoho has no
 * idempotency key, so sending it again means a second copy of every new contact
 * on it. Not blocked, because re-running after a partial failure is a real thing
 * people need to do.
 *
 * Inline rather than a modal: the rows it is talking about are just above, and a
 * dialog would cover them at exactly the moment somebody wants to look again.
 */
function PushConfirm({
  count,
  books,
  noun,
  alreadyPushedAt,
  onConfirm,
  onCancel,
}: {
  count: number;
  books: string;
  noun: string;
  alreadyPushedAt: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="flex flex-col gap-3 rounded-md border border-amber-500/40 bg-amber-500/5 p-4">
      <p className="text-sm">
        This exact file was already pushed on <span className="font-medium">{alreadyPushedAt}</span>.
      </p>
      <p className="text-sm text-muted-foreground">
        Zoho cannot recognise a repeat, so sending it again adds every new {noun.replace(/s$/, "")} on
        it a second time to <span className="font-medium">{books}</span> - and a contact with
        transactions against it cannot be deleted. Continue only if you are re-running a push that did
        not finish.
      </p>

      <div className="flex flex-wrap items-center gap-3">
        <CoButton onClick={onConfirm}>Push {count} again anyway</CoButton>
        <CoButton variant="outline" onClick={onCancel}>
          Cancel
        </CoButton>
      </div>
    </div>
  );
}
